import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { useCuentaContableEmpresaVenta } from "api/CuentaContableEmpresa.hook"
import { useCuentaContableCuentaVenta } from "api/CuentaContable_Cuenta.hook"
import { GetImpuestosDtoProps } from "api/DTOs/GetImpuestosDto"
import { useImpuestoEmpresaNew } from "api/ImpuestoEmpresa.hook"
import { CATEGORIA_CUENTA_CONTABLE_ENUM } from "api/Types/CategoriaCuentaContableEnum"
import { DropdownCuentasContablesConModal } from "common/components/DropdownsCuentasContables/DropdownCuentasContablesConModal/DropdownCuentasContablesConModal"
import { formatCurrency } from "common/utils/currency"
import { FormikDocumentsProps } from "features/Crear/pages/FormCreacionDocumento/CreacionDocumento"
import { MODES_DOCUMENTOS } from "features/Crear/pages/FormCreacionDocumento/constant"
import useCuentasContables from "features/Tesoreria/hooks/useCuentasContables"
import { ArrayHelpers, FieldArray } from "formik"
import React, { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"
import { ArticuloSelectedProps } from "../../../SelectedArticlesDocument/SelectedArticlesDocument"
import ModalDetalleArticuloDocumento from "../ModalDetalleArticuloDocumento/ModalDetalleArticuloDocumento"
import SearchArticuloTableDocument from "./Components/SearchArticuloTableDocument/SearchArticuloTableDocument"

const CellAddRowAction = ({
  onAdd,
  onDelete,
}: {
  onAdd: () => void
  onDelete: () => void
}) => (
  <td>
    <div className='d-flex justify-content-end'>
      <span
        onClick={onAdd}
        className='hover:tw-text-secondary hover:!tw-border !tw-rounded tw-border-secondary tw-w-10 tw-h-10 tw-flex tw-items-center tw-justify-center tw-cursor-pointer'
      >
        <FontAwesomeIcon
          icon={["fal", "pen"]}
          className='tw-w-4 tw-h-4'
        />
      </span>
      <span
        onClick={onDelete}
        className='tw-text-danger hover:!tw-border !tw-rounded tw-border-danger tw-w-10 tw-h-10 tw-flex tw-items-center tw-justify-center tw-cursor-pointer'
      >
        <FontAwesomeIcon
          className='tw-w-4 tw-h-4'
          icon={["fal", "xmark"]}
        />
      </span>
    </div>
  </td>
)

const ArticulosTableDocumento = ({
  values,
  setFieldValue,
}: FormikDocumentsProps) => {
  const { t } = useTranslation()
  const { impuestoEmpresa } = useImpuestoEmpresaNew({
    EmpresaId: values?.empresaActiva?.id,
    TipoImpuestoEmpresa:
      values.documentoType.mode === MODES_DOCUMENTOS.VENTA ? 1 : 0,
    EsActivo: true,
    IsPagingEnabled: false,
  })

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const COLUMNS_NEW_DOCUMENT = (values: $TSMigrationFixMe) => [
    `${t("crear.facturaRec.headerConcept")}`,
    `${t("crear.facturaRec.headerCant")}`,
    ...(!values.config.sinValorar
      ? [
          `${t("crear.facturaRec.headerPrice")}`,
          `${t("crear.facturaRec.headerImp")}`,
          ...(values.config.cuentaContableMultiple
            ? [`${t("crear.documento.cuentaContable")}`]
            : []),
          `${t("crear.facturaRec.headerImport")}`,
        ]
      : []),
    "",
  ]

  const { getCuentaContablePorDefecto } = useCuentasContables()
  const { cuentasContablesEmpresas: listaDeCuentasBase } =
    useCuentaContableCuentaVenta()
  const {
    cuentasContablesEmpresas: listaDeCuentas,
    cuentasContablesEmpresasRefetch: refreshListaDeCuentas,
  } = useCuentaContableEmpresaVenta()

  const initialPorDefectoCuentasVentas = getCuentaContablePorDefecto(
    CATEGORIA_CUENTA_CONTABLE_ENUM.VENTA
  )
  const initialPorDefectoDocumento = listaDeCuentas?.find(
    (cuentaContableEmpresaBase) => {
      return cuentaContableEmpresaBase?.id === initialPorDefectoCuentasVentas
    }
  )

  const [showModalDetail, setShowModalDetail] = useState<{
    visible: boolean
    data: ArticuloSelectedProps | null
  }>({
    visible: false,
    data: null,
  })

  const recalculateTotal = useCallback(
    (updatedConceptos: ArticuloSelectedProps[]) => {
      const totalValue = updatedConceptos.reduce(
        (total: number, concepto: ArticuloSelectedProps) => {
          return total + concepto.cantidad * concepto.precioVenta
        },
        0
      )
      const cantidadValue = updatedConceptos.reduce(
        (total: number, concepto: ArticuloSelectedProps) => {
          return total + concepto.cantidad
        },
        0
      )

      setFieldValue("total", totalValue)
      setFieldValue("cantidadTotalArticulos", cantidadValue)
    },
    [setFieldValue]
  )

  return (
    <>
      <table className='tw-w-full'>
        <thead
          className={`${
            !values.conceptos?.length && "tw-opacity-50"
          } !tw-rounded-t-md`}
        >
          <tr
            className='tw-h-12 tw-w-full !tw-rounded-t-md'
            style={{ backgroundColor: values.config.colorSelected }}
          >
            {COLUMNS_NEW_DOCUMENT(values).map((header, index) => {
              const isFirst = index === 0
              const isLast = index === COLUMNS_NEW_DOCUMENT(values).length - 1
              return (
                <th
                  className={`tw-px-3 tw-text-simple-10 !tw-text-nowrap !tw-font-normal  ${
                    isFirst ? "!tw-rounded-tl-md tw-text-left" : "tw-text-right"
                  } ${isLast ? "!tw-rounded-tr-md" : ""}`}
                  key={header}
                >
                  {header}
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          <FieldArray name='conceptos'>
            {({ remove }: ArrayHelpers) =>
              values.conceptos?.map(
                (concepto: ArticuloSelectedProps, index: number) => (
                  <tr
                    key={concepto.id}
                    className='tw-border-b tw-border-neutral-25 tw-h-12 tw-py-auto'
                  >
                    <td className='tw-text-left tw-px-2'>
                      {concepto.concepto}
                    </td>

                    <td className='tw-px-2 tw-text-right'>
                      {concepto.cantidad}
                    </td>

                    {!values.config.sinValorar && (
                      <>
                        <td className='tw-px-2 tw-text-right'>
                          {formatCurrency(
                            concepto.precioVenta,
                            false,
                            values.divisa
                          )}
                        </td>
                        <td className='tw-px-2 tw-text-right'>
                          {concepto.impuestoArticuloTipoId && (
                            <>
                              {impuestoEmpresa?.items?.map(
                                // @ts-expect-error TS(2345): Argument of type '(taxe: GetImpuestosDtoProps) => ...
                                (taxe: GetImpuestosDtoProps) => {
                                  if (
                                    taxe.id === concepto.impuestoArticuloTipoId
                                  ) {
                                    return (
                                      <React.Fragment key={taxe.id}>
                                        {t("impuestos.table.typeGroup.iva")} (
                                        {taxe.valor} %)
                                      </React.Fragment>
                                    )
                                  }
                                  return null
                                }
                              )}
                            </>
                          )}
                        </td>

                        {values?.config?.cuentaContableMultiple && (
                          <td className='tw-px-2 !tw-max-w-72 !tw-w-72'>
                            <DropdownCuentasContablesConModal
                              cuentasContablesOptions={listaDeCuentas}
                              dropdownValue={concepto?.cuentaContableId}
                              onChange={({ value }: { value: string }) => {
                                setFieldValue(
                                  `conceptos.[${index}].cuentaContableId`,
                                  value
                                )
                              }}
                              listaDeCuentasBase={listaDeCuentasBase}
                              cuentaPorDefecto={initialPorDefectoDocumento}
                              refreshFetchData={refreshListaDeCuentas}
                              withCreateCuenta
                            />
                          </td>
                        )}

                        <td className='tw-px-2 tw-text-right'>
                          {formatCurrency(concepto.total, false, values.divisa)}
                        </td>
                      </>
                    )}

                    <CellAddRowAction
                      onAdd={() =>
                        setShowModalDetail({
                          visible: !showModalDetail.visible,
                          data: concepto,
                        })
                      }
                      onDelete={() => remove(index)}
                    />
                  </tr>
                )
              )
            }
          </FieldArray>
        </tbody>
      </table>

      <SearchArticuloTableDocument
        values={values}
        setFieldValue={setFieldValue}
      />

      <ModalDetalleArticuloDocumento
        modalDetail={showModalDetail}
        onHide={() =>
          setShowModalDetail({
            visible: false,
            data: null,
          })
        }
        typeDocument={values.documentoType.type}
        onChange={(event: ArticuloSelectedProps) => {
          const updatedConceptos = values.conceptos
            .map((concepto: ArticuloSelectedProps) => {
              if (concepto.index === event.index) {
                return { ...event, total: event.precioVenta * event.cantidad }
              }
              return concepto
            })
            .filter((concepto: ArticuloSelectedProps) => concepto.cantidad > 0)

          setFieldValue("conceptos", updatedConceptos)
          recalculateTotal(updatedConceptos)
        }}
        librador={values.librador}
        currency={values.divisa}
      />
    </>
  )
}

export default ArticulosTableDocumento
